import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { Contact } from '../../contactos/models/contacto.model';
import { AuthService } from '../../auth-pages/services/auth.service';

export type ContactPickerMode = 'INCLUDE' | 'EXCLUDE';

@Component({
  selector: 'app-contact-picker-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './contact-picker-modal.component.html',
})
export class ContactPickerModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() mode: ContactPickerMode = 'INCLUDE';
  /** IDs ya adjuntos o excluidos — no se ofrecen para selección */
  @Input() excludeContactIds: number[] = [];

  @Output() confirmed = new EventEmitter<number[]>();
  @Output() cancelled = new EventEmitter<void>();

  private readonly api = inject(ContactoApiService);
  private readonly auth = inject(AuthService);

  loading = false;
  error = '';

  rows: Contact[] = [];
  selectedIds = new Set<number>();

  searchTerm = '';
  page = 0;
  pageSize = 25;
  hasMore = false;
  totalElements = 0;

  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(() => this.fetchPage(true)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (changes['isOpen'].currentValue) {
        this.reset();
        this.fetchPage(true).subscribe();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(): void {
    this.search$.next(this.searchTerm.trim());
  }

  toggle(id: number | undefined): void {
    if (id === undefined) return;
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  isSelected(id: number | undefined): boolean {
    return id !== undefined && this.selectedIds.has(id);
  }

  isAlreadyAttached(id: number | undefined): boolean {
    return id !== undefined && this.excludeContactIds.includes(id);
  }

  loadMore(): void {
    if (!this.hasMore || this.loading) return;
    this.page++;
    this.fetchPage(false).subscribe();
  }

  confirm(): void {
    if (this.selectedIds.size === 0) return;
    this.confirmed.emit([...this.selectedIds]);
  }

  cancel(): void {
    this.cancelled.emit();
  }

  get title(): string {
    return this.mode === 'EXCLUDE' ? 'Excluir contactos' : 'Agregar contactos';
  }

  get confirmLabel(): string {
    const n = this.selectedIds.size;
    if (n === 0) {
      return this.mode === 'EXCLUDE' ? 'Excluir' : 'Agregar';
    }
    return this.mode === 'EXCLUDE' ? `Excluir ${n}` : `Agregar ${n}`;
  }

  initialsOf(c: Contact): string {
    const src = `${c.fullname || ''} ${c.lastname || ''}`.trim() || c.mail || '?';
    return src.split(/\s+/).map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  }

  fullNameOf(c: Contact): string {
    return `${c.fullname || ''} ${c.lastname || ''}`.trim() || '—';
  }

  private reset(): void {
    this.searchTerm = '';
    this.page = 0;
    this.rows = [];
    this.selectedIds.clear();
    this.error = '';
    this.hasMore = false;
    this.totalElements = 0;
  }

  private fetchPage(reset: boolean) {
    const businessId = this.auth.getBusinessId();
    if (!businessId) {
      this.error = 'No se pudo obtener el business actual.';
      return of(null);
    }
    if (reset) {
      this.page = 0;
      this.rows = [];
    }
    this.loading = true;
    this.error = '';
    return this.api
      .searchContacts({
        businessId,
        searchTerm: this.searchTerm.trim() || undefined,
        isActive: true,
        page: this.page,
        size: this.pageSize,
      })
      .pipe(
        catchError(err => {
          this.loading = false;
          this.error = err?.error?.message || 'No se pudieron cargar los contactos.';
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .pipe(
        switchMap(res => {
          this.loading = false;
          if (!res) return of(null);
          this.rows = reset ? res.content : [...this.rows, ...res.content];
          this.totalElements = res.totalElements;
          this.hasMore = !res.last;
          return of(res);
        }),
      );
  }
}
